import AddLocationAltOutlined from "@mui/icons-material/AddLocationAltOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import PersonAdd from "@mui/icons-material/PersonAdd";
import {
  Alert,
  Stack,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AssociateFacilitiesDialog } from "components/AssociateFacilities/AssociateFacilitiesDialog";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { useSubmissionFormContext } from "contexts/SubmissionFormContext";
import { RecipientType } from "encamp-shared/src/constants/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  SignatureType,
  SubmissionMethod,
  SubmissionType,
  ReviewMode,
} from "generated-graphql/graphql";
import { SubmissionReviewerSigner, SubmissionFormData } from "hooks/useSubmissionForm";
import { DateTime } from "luxon";
import React, { useState, useMemo } from "react";
import { Controller, Control, useWatch, UseFormSetValue } from "react-hook-form";

import { Reviewer, ReviewerPicker } from "./ReviewersEditList";
import { ReviewersDatagrid } from "./ReviewersList";
import { SignersDatagrid } from "./SignersDatagrid";
import { SubmissionFacilitiesDatagrid } from "./SubmissionFacilitiesDatagrid";

interface SubmissionFormFieldsProps {
  control: Control<SubmissionFormData>;
  reviewRequired: boolean;
  signatureType: SignatureType;
  disabled?: boolean;
  isStaff?: boolean;
  onReviewerClick: () => void;
  onSignerClick: () => void;
  setValue?: UseFormSetValue<SubmissionFormData>;
  setReviewers: (reviewers: Reviewer[]) => void;
  reviewers: Reviewer[];
  reviewMode: ReviewMode;
  signer: SubmissionReviewerSigner | null;
  setSigner: (signer: SubmissionReviewerSigner | null) => void;
  staffAssignee: { id: string; name: string; email: string } | null;
  setStaffAssignee: (assignee: { id: string; name: string; email: string } | null) => void;
  submissionDueDate?: DateTime;
  canEditSubmissionType: boolean;
  canEditFacilities: boolean;
}

export function SubmissionFormFields({
  control,
  reviewRequired,
  signatureType,
  disabled = false,
  isStaff = false,
  onReviewerClick,
  onSignerClick,
  reviewers,
  setReviewers,
  reviewMode,
  setValue,
  signer,
  setSigner,
  staffAssignee,
  setStaffAssignee,
  submissionDueDate,
  canEditSubmissionType,
  canEditFacilities,
}: SubmissionFormFieldsProps) {
  const [associateFacilitiesOpen, setAssociateFacilitiesOpen] = useState(false);
  const canEditInThisState = !disabled;
  const canEditSubmissionTypeInThisState = canEditSubmissionType && canEditInThisState;
  const canEditFacilitiesInThisState = canEditFacilities && canEditInThisState;
  const facilityIds: string[] = useWatch({
    control,
    name: "facilityIds",
    defaultValue: [],
  });
  const { issues } = useSubmissionFormContext();

  const reviewRequiredIssues = useMemo(
    () => issues.filter((i) => i.key === "reviewRequired"),
    [issues]
  );

  const signatureTypeIssues = useMemo(
    () => issues.filter((i) => i.key === "signatureType"),
    [issues]
  );
  const handleFacilitySelect = (selectedFacilityIds: string[]) => {
    if (setValue) {
      setValue("facilityIds", selectedFacilityIds, { shouldValidate: true });
    }
    setAssociateFacilitiesOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Stack spacing={3}>
        <Box>
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                disabled={disabled}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                variant="outlined"
                label="Title *"
                placeholder="Title *"
              />
            )}
          />
        </Box>

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Facility *
              {!canEditFacilities && (
                <Tooltip
                  title="Set by requirement — cannot be changed"
                  arrow
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: (theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        boxShadow: theme.shadows[5],
                      }),
                    },
                  }}
                >
                  <InfoOutlined
                    fontSize="small"
                    color="disabled"
                    sx={{ ml: 0.5, verticalAlign: "middle" }}
                  />
                </Tooltip>
              )}
            </Typography>

            {canEditFacilitiesInThisState && (
              <IconButton
                size="small"
                sx={{ color: "primary.main" }}
                onClick={() => setAssociateFacilitiesOpen(true)}
                title="Add facilities"
              >
                <AddLocationAltOutlined fontSize="small" />
              </IconButton>
            )}
          </Box>

          <SubmissionFacilitiesDatagrid
            facilityIds={facilityIds}
            onRemoveFacility={
              canEditFacilitiesInThisState
                ? (facilityId) => {
                    if (setValue) {
                      const updatedIds = facilityIds.filter((id) => id !== facilityId);
                      setValue("facilityIds", updatedIds, {
                        shouldValidate: true,
                      });
                    }
                  }
                : undefined
            }
            onAddFacility={
              canEditFacilitiesInThisState ? () => setAssociateFacilitiesOpen(true) : undefined
            }
          />
        </Box>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Controller
              name="dueDate"
              control={control}
              render={({ field, fieldState }) => {
                const issue = issues.find(
                  (i) => i.key === "dueDate" && i.modelName === "Submission"
                );
                const hasError = !!issue;
                return (
                  <>
                    <DatePicker
                      value={field.value || null}
                      onChange={field.onChange}
                      label="Due Date *"
                      disabled={disabled}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          variant: "outlined",
                          placeholder: "Due Date",
                          error: hasError,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              "&.Mui-focused": {
                                boxShadow: "none",
                              },
                              "&:hover": {
                                boxShadow: "none",
                              },
                            },
                          },
                        },
                      }}
                    />
                    {hasError && (
                      <Typography variant="caption" color="error" ml="14px">
                        {issue?.message}
                      </Typography>
                    )}
                  </>
                );
              }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Controller
              name="submissionType"
              control={control}
              disabled={!canEditSubmissionTypeInThisState}
              render={({ field, fieldState }) => (
                <>
                  <FormControl fullWidth size="small" error={!!fieldState.error}>
                    <InputLabel>
                      <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                        Type of Submission *
                        {!canEditSubmissionType && (
                          <Tooltip
                            title="The submission type cannot be changed when the submission is associated with a system-generated task"
                            placement="top"
                            arrow
                          >
                            <InfoOutlined
                              sx={{
                                ml: 0.5,
                                verticalAlign: "middle",
                                cursor: "pointer",
                                backgroundColor: "background.paper",
                                borderRadius: "50%",
                                padding: "2px",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value || ""}
                      label="Type of submission *"
                      disabled={!canEditSubmissionTypeInThisState}
                    >
                      {Object.values(SubmissionType).map((value) => (
                        <MenuItem key={value} value={value}>
                          {prettyPrintEnumValue(value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {fieldState.error && <ErrorDisplay error={fieldState.error} variant="caption" />}
                </>
              )}
            />
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Controller
              name="recipient"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormControl fullWidth size="small" error={!!fieldState.error}>
                    <InputLabel>Recipient *</InputLabel>
                    <Select {...field} label="Recipient *" disabled={disabled}>
                      <MenuItem value={RecipientType.REGULATORY_AGENCY}>Regulatory Agency</MenuItem>
                      <MenuItem value={RecipientType.THIRD_PARTY}>Third Party</MenuItem>
                      <MenuItem value={RecipientType.INTERNAL}>Internal</MenuItem>
                      <MenuItem value={RecipientType.OTHER}>Other</MenuItem>
                    </Select>
                  </FormControl>
                  {fieldState.error && <ErrorDisplay error={fieldState.error} variant="caption" />}
                </>
              )}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Controller
              name="submissionMethod"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <FormControl fullWidth size="small" error={!!fieldState.error}>
                    <InputLabel>Submission method *</InputLabel>
                    <Select
                      {...field}
                      value={field.value || ""}
                      label="Submission method *"
                      disabled={disabled}
                    >
                      {Object.values(SubmissionMethod).map((value) => (
                        <MenuItem key={value} value={value}>
                          {prettyPrintEnumValue(value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {fieldState.error && <ErrorDisplay error={fieldState.error} variant="caption" />}
                </>
              )}
            />
          </Box>
        </Stack>

        <Stack spacing={2}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Reviewer(s) & Signer
          </Typography>

          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Encamp Assignee
            </Typography>
            <ReviewerPicker
              onChange={(reviewer) => {
                if (reviewer) {
                  setStaffAssignee({
                    id: reviewer.id,
                    name: reviewer.name,
                    email: reviewer.email,
                  });
                } else {
                  setStaffAssignee(null);
                }
              }}
              value={staffAssignee ? { ...staffAssignee, order: 0, isStaff: true } : null}
              staffOnly={true}
              disabled={disabled}
              sx={{ "& + div": { display: "none" } }}
            />
          </Box>

          {isStaff && (
            <Box>
              <Controller
                name="peerReviewRequired"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => {
                          if (disabled) return;
                          field.onChange(e.target.checked);
                          if (e.target.checked && !reviewRequired) {
                            setValue?.("reviewRequired", true);
                          }
                        }}
                        size="small"
                        disabled={disabled}
                      />
                    }
                    label={<Typography variant="body2">Require peer review</Typography>}
                  />
                )}
              />
            </Box>
          )}

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Reviewer
              </Typography>
              <IconButton
                size="small"
                onClick={onReviewerClick}
                disabled={!reviewRequired || disabled}
                sx={{ color: reviewRequired ? "primary.main" : "inherit" }}
              >
                <PersonAdd fontSize="small" />
              </IconButton>
            </Stack>
            <Controller
              name="reviewRequired"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  value={field.value ? "true" : "false"}
                  onChange={(e) => {
                    if (disabled) return;
                    field.onChange(e.target.value === "true");
                  }}
                  row
                >
                  <FormControlLabel
                    value="false"
                    disabled={disabled}
                    control={<Radio size="small" disabled={disabled} />}
                    label={<Typography variant="body2">No review required</Typography>}
                  />
                  <FormControlLabel
                    value="true"
                    disabled={disabled}
                    control={<Radio size="small" disabled={disabled} />}
                    label={<Typography variant="body2">Include reviewers</Typography>}
                  />
                </RadioGroup>
              )}
            />
            {reviewRequired && (
              <Box>
                <ReviewersDatagrid
                  reviewers={reviewers}
                  setReviewers={disabled ? undefined : setReviewers}
                  reviewMode={reviewMode}
                  setValue={disabled ? undefined : setValue}
                  handleAddReviewer={onReviewerClick}
                  submissionDueDate={submissionDueDate}
                  disabled={disabled}
                  isStaff={isStaff}
                />
                {reviewRequiredIssues.length > 0 && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {reviewRequiredIssues.map((i, index) => (
                      <div key={index}>{i.message}</div>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </Box>

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Signer
              </Typography>
              <IconButton
                size="small"
                onClick={onSignerClick}
                disabled={signatureType === SignatureType.None || disabled}
                sx={{
                  color: signatureType === SignatureType.None ? "inherit" : "primary.main",
                }}
              >
                <PersonAdd fontSize="small" />
              </IconButton>
            </Stack>
            <Controller
              name="signatureType"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  row
                  onChange={(e) => {
                    if (disabled) return;
                    field.onChange(e.target.value);
                  }}
                >
                  <FormControlLabel
                    value={SignatureType.None}
                    disabled={disabled}
                    control={<Radio size="small" disabled={disabled} />}
                    label={<Typography variant="body2">No signature</Typography>}
                  />
                  <FormControlLabel
                    value={SignatureType.Digital}
                    disabled={disabled}
                    control={<Radio size="small" disabled={disabled} />}
                    label={<Typography variant="body2">Digital signature</Typography>}
                  />
                  <FormControlLabel
                    value={SignatureType.WetInk}
                    disabled={disabled}
                    control={<Radio size="small" disabled={disabled} />}
                    label={<Typography variant="body2">Wet-ink signature</Typography>}
                  />
                </RadioGroup>
              )}
            />
            {signatureType !== SignatureType.None && (
              <Box>
                <SignersDatagrid
                  signers={signer ? [signer] : []}
                  signatureType={signatureType}
                  handleAddSigner={onSignerClick}
                  handleRemoveSigner={(_signer) => {
                    if (disabled) return;
                    setSigner(null);
                  }}
                  submissionDueDate={submissionDueDate}
                  disabled={disabled}
                />
                {signatureTypeIssues.length > 0 && (
                  <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                    {signatureTypeIssues.map((i, index) => (
                      <div key={index}>{i.message}</div>
                    ))}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </Stack>
      </Stack>

      <AssociateFacilitiesDialog
        open={associateFacilitiesOpen}
        onClose={() => setAssociateFacilitiesOpen(false)}
        existingFacilityIds={facilityIds}
        onSave={handleFacilitySelect}
        subtitle="Select facilities to associate with this submission."
        excludeSecondaryWasteFacilities
      />
    </LocalizationProvider>
  );
}
